import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';

export const guestGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const storage = inject(StorageService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  if (authService.isAdmin()) {
    return router.createUrlTree(['/admin/dashboard']);
  }

  // Owner ya autenticado y con el negocio en caché → panel del owner.
  // NO saltamos directo al negocio: /login pertenece al dominio central, y
  // mandar al tenant desde acá dejaba el panel del owner inalcanzable. Entrar
  // a un negocio es una acción explícita desde /owner/businesses.
  const tenant = storage.getTenant();
  if (tenant?.slug) {
    return router.createUrlTree(['/owner/businesses']);
  }

  // Sin negocio en caché NO significa "owner nuevo". El token vive en el
  // localStorage de este origen, pero el negocio pudo haberse guardado en el
  // origen del subdominio del tenant, o la caché pudo haberse limpiado.
  // Antes se asumía onboarding y eso hacía que un owner con negocio creara
  // uno duplicado. Lo preguntamos al API, que es la única fuente de verdad.
  return authService.getMyTenants().pipe(
    map((response) => {
      const tenants = (response?.tenants ?? []).filter((t) => !!t.slug);

      if (tenants.length === 0) {
        return router.createUrlTree(['/onboarding']);
      }

      const principal = tenants.find((t) => t.activo !== false) ?? tenants[0];
      storage.setTenant(principal);
      return router.createUrlTree(['/owner/businesses']);
    }),
    catchError((error) => {
      // 401/403: el token quedó viejo o revocado. Lo tiramos y mostramos el
      // login en vez de mandar a onboarding con una sesión muerta.
      if (error?.status === 401 || error?.status === 403) {
        authService.clearSessionSilently();
        return of(true);
      }

      // Cualquier otro error (API caída, red): dejamos ver el login en vez de
      // bloquear al usuario en una pantalla que no puede resolver.
      console.error('guestGuard: no se pudieron consultar los negocios del owner', error);
      return of(true);
    })
  );
};
